import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUp,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  FileText,
  Sparkles,
  Building2,
  Users,
  ListChecks,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import InviktusLogo from "@/components/InviktusLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  createSession,
  getSession,
  sendMessage,
  finalizeSession,
} from "@/lib/api";

const CONSULTANT_AVATAR =
  "/brand/consulting-hero.webp";

export default function ConsultingChat() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sid = params.get("session");

  const [session, setSession] = useState(null);
  const [progress, setProgress] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // ---- Live percentage counter ------------------------------------------------
  // While the user types activity weights (eg. "30%, 30%, 15%, 20%, 10%") we
  // surface the running total so they can self-correct before sending the
  // message. Mirrors the deterministic Python-side validator in agent.py.
  const pctTotals = useMemo(() => {
    const re = /(?<![\w.])(\d{1,3}(?:[.,]\d{1,2})?)\s*%/g;
    const matches = [...(input || "").matchAll(re)];
    const vals = matches
      .map((m) => parseFloat(m[1].replace(",", ".")))
      .filter((v) => Number.isFinite(v) && v > 0 && v <= 100);
    if (vals.length < 2) return null;
    const sum = Math.round(vals.reduce((s, v) => s + v, 0) * 100) / 100;
    return { vals, sum, count: vals.length };
  }, [input]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        if (sid) {
          const data = await getSession(sid);
          if (cancelled) return;
          setSession(data.session);
          setProgress(data.progress);
        } else {
          const data = await createSession();
          if (cancelled) return;
          setSession(data.session);
          setProgress(data.progress);
          setParams({ session: data.session.id }, { replace: true });
        }
      } catch (e) {
        console.error(e);
        toast.error("No fue posible cargar la sesión.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session?.messages?.length, sending]);

  const completed = session?.status === "completed";
  const sessionId = session?.id;

  const onSend = async () => {
    const text = input.trim();
    if (!text || !sessionId || sending || completed) return;
    setSending(true);
    const optimisticMessages = [
      ...(session?.messages || []),
      { role: "user", content: text, ts: new Date().toISOString() },
    ];
    setSession({ ...session, messages: optimisticMessages });
    setInput("");
    try {
      const data = await sendMessage(sessionId, text);
      setSession(data.session);
      setProgress(data.progress);
      if (data.completed) {
        toast.success("Diagnóstico completo. Generando informe ejecutivo...");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error al enviar el mensaje. Intente de nuevo.");
      setSession({ ...session, messages: session?.messages || [] });
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const onFinalize = async () => {
    if (!sessionId) return;
    try {
      setFinalizing(true);
      // Fire and forget — the LLM extraction can exceed the proxy timeout for
      // rich multi-person sessions. The /informe/:sid page already polls and
      // retries finalize gracefully, so we navigate immediately regardless.
      finalizeSession(sessionId).catch(() => {});
      toast.success("Generando informe ejecutivo. Esto puede tomar 30-90 segundos.");
      // Give the request a head start before navigating
      setTimeout(() => navigate(`/informe/${sessionId}`), 600);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo iniciar la generación del informe.");
    } finally {
      setFinalizing(false);
    }
  };

  const onViewReport = () => sessionId && navigate(`/informe/${sessionId}`);

  const overallPct = useMemo(() => {
    if (!progress) return 0;
    return Math.round((progress.covered / progress.total) * 100);
  }, [progress]);

  const stages = progress?.stage?.stages || [];
  const currentStageIdx = progress?.stage?.current_index ?? 0;
  const struct = progress?.structure || { departments: 0, people: 0, activities: 0 };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white text-slate-900" data-testid="consulting-chat">
      {/* SIDEBAR */}
      <aside
        className="hidden h-full w-[340px] shrink-0 flex-col border-r border-slate-800 bg-slate-950 text-slate-50 lg:flex"
        data-testid="chat-sidebar"
      >
        <div className="px-7 pt-7">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-400 hover:text-slate-200 transition"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Inicio
          </button>
          <div className="mt-6">
            <InviktusLogo className="text-slate-50" size={24} tone="light" />
          </div>
          <Separator className="mt-7 bg-slate-800" />
        </div>

        <div className="px-7 pt-6">
          <p className="eyebrow text-accent-blue-light">Diagnóstico ABC</p>
          <h2 className="mt-3 font-display text-2xl font-medium leading-tight text-slate-50">
            Mapeo en curso
          </h2>

          {/* Structural counters */}
          <div className="mt-6 grid grid-cols-3 gap-2 border border-slate-800 bg-slate-900/40 p-3" data-testid="structure-counters">
            <CounterCell icon={Building2} value={struct.departments} label="Dptos." />
            <CounterCell icon={Users} value={struct.people} label="Personas" />
            <CounterCell icon={ListChecks} value={struct.activities} label="Actividades" />
          </div>

          {/* Overall progress */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-400">
              <span>Cobertura</span>
              <span className="font-mono text-slate-200" data-testid="progress-counter">
                {progress?.covered ?? 0}/{progress?.total ?? 47}
              </span>
            </div>
            <Progress
              value={overallPct}
              className="mt-3 h-1 rounded-none bg-slate-800 [&>div]:bg-teal-500"
              data-testid="overall-progress-bar"
            />
            <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {overallPct}% de preguntas guía cubiertas
            </p>
          </div>
        </div>

        {/* Stages */}
        <div className="mt-7 flex-1 overflow-y-auto thin-scroll px-7 pb-6">
          <p className="eyebrow text-slate-500">Etapas del diagnóstico</p>
          <ol className="mt-4 space-y-3.5">
            {stages.map((st, i) => {
              const done = i < currentStageIdx;
              const active = i === currentStageIdx;
              return (
                <li key={st.id} data-testid={`stage-${st.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center pt-0.5">
                      <div
                        className={`flex h-5 w-5 items-center justify-center border ${
                          done
                            ? "border-teal-500 bg-teal-500/15 text-accent-blue-light"
                            : active
                            ? "border-teal-400 bg-teal-400 text-slate-950"
                            : "border-slate-700 text-slate-600"
                        }`}
                      >
                        {done ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <span className="font-mono text-[10px]">{i + 1}</span>
                        )}
                      </div>
                      {i < stages.length - 1 && (
                        <span
                          className={`mt-1 h-6 w-px ${
                            done ? "bg-teal-500/40" : "bg-slate-800"
                          }`}
                        />
                      )}
                    </div>
                    <div className="min-w-0 pb-1">
                      <p
                        className={`font-display text-sm font-medium ${
                          active ? "text-slate-50" : done ? "text-slate-200" : "text-slate-400"
                        }`}
                      >
                        {st.label}
                      </p>
                      <p className="mt-1 text-[11px] leading-snug text-slate-500">
                        {st.desc}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="border-t border-slate-800 px-7 py-5">
          {completed ? (
            <Button
              onClick={onViewReport}
              className="w-full rounded-sm bg-teal-600 text-sm font-semibold uppercase tracking-[0.2em] text-white hover:bg-teal-500"
              data-testid="view-report-btn-sidebar"
            >
              <FileText className="mr-2 h-4 w-4" />
              Ver informe ejecutivo
            </Button>
          ) : (
            <Button
              onClick={onFinalize}
              disabled={!progress || progress.covered === 0 || finalizing}
              variant="outline"
              className="w-full rounded-sm border-slate-700 bg-transparent text-xs uppercase tracking-[0.2em] text-slate-200 hover:bg-slate-800"
              data-testid="finalize-now-btn"
            >
              {finalizing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Generar informe ahora
            </Button>
          )}
          <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Sesión: <span className="font-mono normal-case text-slate-400">{sessionId || "—"}</span>
          </p>
        </div>
      </aside>

      {/* MAIN CHAT AREA */}
      <section className="flex h-full min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4 lg:px-10">
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <button
              onClick={() => navigate("/")}
              className="text-slate-400 hover:text-slate-700 lg:hidden"
              aria-label="Volver"
              data-testid="back-mobile"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="lg:hidden">
              <InviktusLogo size={22} className="text-slate-900" />
            </div>
            <div className="hidden lg:block">
              <p className="eyebrow">Entrevista consultiva</p>
              <p className="mt-1 font-display text-lg font-medium text-slate-900">
                Agente Consultor · Activity-Based Costing
              </p>
            </div>
          </div>
          <div className="hidden items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 md:flex">
            <span>Etapa actual:</span>
            <span className="font-semibold text-accent-blue">
              {stages[currentStageIdx]?.label || "—"}
            </span>
          </div>
        </header>

        {/* Mobile progress bar */}
        <div className="border-b border-slate-200 px-6 py-3 lg:hidden">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-500">
            <span>{stages[currentStageIdx]?.label || "Diagnóstico"}</span>
            <span className="font-mono text-slate-900">
              {struct.departments}D · {struct.people}P · {struct.activities}A
            </span>
          </div>
          <Progress
            value={overallPct}
            className="mt-2 h-1 rounded-none bg-slate-100 [&>div]:bg-teal-600"
          />
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto thin-scroll px-6 pt-10 pb-6 lg:px-16"
          data-testid="messages-container"
        >
          <div className="mx-auto max-w-3xl space-y-12">
            {loading ? (
              <div className="flex items-center justify-center py-24 text-slate-500">
                <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                <span className="text-sm uppercase tracking-[0.2em]">Cargando entrevista...</span>
              </div>
            ) : (
              <>
                {(session?.messages || []).map((m, i) => (
                  <MessageBlock key={m.id || m.ts || `msg-${i}`} message={m} />
                ))}
                {sending && <TypingIndicator />}
                {completed && (
                  <div className="msg-enter mt-12 border border-teal-700/30 bg-teal-50/60 p-6">
                    <p className="eyebrow text-accent-blue">Diagnóstico completo</p>
                    <p className="mt-2 font-display text-xl font-medium text-slate-900">
                      El análisis ABC está terminado.
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                      Su informe ejecutivo, junto con el Excel detallado y el gráfico de actividades por
                      colaborador, ya está disponible para descarga.
                    </p>
                    <Button
                      onClick={onViewReport}
                      className="mt-5 rounded-sm bg-slate-900 text-xs uppercase tracking-[0.2em] text-white hover:bg-slate-800"
                      data-testid="view-report-inline-btn"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Ver informe ejecutivo
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white px-6 py-5 lg:px-16">
          <div className="mx-auto max-w-3xl">
            {pctTotals && (() => {
              const off = Math.round((100 - pctTotals.sum) * 100) / 100;
              const ok = Math.abs(off) < 0.005;
              return (
                <div
                  className={`mb-3 inline-flex items-center gap-2 border px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] ${
                    ok
                      ? "border-teal-700/40 bg-teal-50 text-teal-800"
                      : "border-amber-500/60 bg-amber-50 text-amber-800"
                  }`}
                  data-testid="pct-counter"
                  title="Suma de los porcentajes que escribiste en este mensaje"
                >
                  <span>{ok ? "✓" : "⚠"} suma {pctTotals.count} %</span>
                  <span className="font-mono text-[12px]">
                    {pctTotals.sum.toFixed(pctTotals.sum % 1 === 0 ? 0 : 2)}%
                  </span>
                  {!ok && (
                    <span className="font-mono text-[11px]">
                      ({off > 0 ? "falta" : "sobra"} {Math.abs(off).toFixed(off % 1 === 0 ? 0 : 2)})
                    </span>
                  )}
                </div>
              );
            })()}
            <div className="relative flex items-end gap-3 border border-slate-300 bg-white focus-within:border-slate-900">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={
                  completed
                    ? "El diagnóstico ya está cerrado."
                    : "Escriba su respuesta... (Enter para enviar, Shift+Enter para nueva línea)"
                }
                disabled={completed || sending || loading}
                rows={2}
                className="min-h-[64px] resize-none border-0 bg-transparent px-5 py-4 text-base leading-relaxed text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="chat-input"
              />
              <Button
                onClick={onSend}
                disabled={!input.trim() || sending || completed || loading}
                className="m-2 h-11 w-11 shrink-0 rounded-sm bg-slate-900 p-0 text-white hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
                aria-label="Enviar"
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-3 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
              <span>Sus respuestas son tratadas con confidencialidad consultiva.</span>
              <span className="text-slate-300">·</span>
              <button
                type="button"
                disabled={completed || sending || loading}
                onClick={() =>
                  setInput((current) =>
                    (current ? current.trim() + "\n\n" : "") +
                    "Quisiera agregar otra persona al diagnóstico. ¿Podemos abrir el mapeo de un nuevo cargo?",
                  )
                }
                className="text-accent-blue hover:text-accent-blue disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="add-collaborator-btn"
              >
                + Agregar otra persona
              </button>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function CounterCell({ icon: Icon, value, label }) {
  return (
    <div className="flex flex-col items-center px-1 py-2">
      <Icon className="h-3.5 w-3.5 text-teal-400" strokeWidth={1.5} />
      <span className="mt-1.5 font-display text-xl font-semibold text-slate-50">{value}</span>
      <span className="mt-0.5 text-[9px] uppercase tracking-[0.18em] text-slate-500">{label}</span>
    </div>
  );
}

function MessageBlock({ message }) {
  if (message.role === "consultant") {
    return (
      <div className="msg-enter flex gap-5" data-testid="consultant-message">
        <img
          src={CONSULTANT_AVATAR}
          alt="Agente Consultor"
          className="h-11 w-11 shrink-0 rounded-sm object-cover grayscale"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-base font-semibold text-slate-900">
              Agente Consultor
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Activity-Based Costing
            </span>
          </div>
          <div className="mt-3 whitespace-pre-wrap font-display text-[17px] leading-[1.7] text-slate-800">
            {message.content}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="msg-enter flex justify-end" data-testid="user-message">
      <div className="max-w-[80%] border border-slate-200 bg-slate-50 px-5 py-3.5">
        <p className="eyebrow text-slate-400">Su respuesta</p>
        <p className="mt-1.5 whitespace-pre-wrap text-[15px] leading-relaxed text-slate-800">
          {message.content}
        </p>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-5" data-testid="typing-indicator">
      <img
        src={CONSULTANT_AVATAR}
        alt=""
        aria-hidden
        className="h-11 w-11 shrink-0 rounded-sm object-cover grayscale opacity-60"
      />
      <div className="flex items-center gap-1 pt-3">
        <span className="typing-dot"></span>
        <span className="typing-dot"></span>
        <span className="typing-dot"></span>
        <span className="ml-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">
          Francesco está escribiendo
        </span>
      </div>
    </div>
  );
}
